import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessagePlugin, Select, SelectValue } from 'tdesign-react';
import FormItem from 'tdesign-react/es/form/FormItem';

import { MissionType } from '@/const/mission';
import { IScene } from '@/interface';
import { CgiErrorCode } from '@/services/cgi';
import { getDiffList, getSceneDiffDetail } from '@/services/diff';

import DiffJsonView from './diffView';
import styles from './index.module.less'; // 使用 CSS Modules 方式引入样式

// 类型定义
interface Chapter {
  value: string;
  label: string;
  scenes: {
    value: string;
    label: string;
  }[]
}

const ChapterSceneDiffView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('storyId');

  // 状态管理
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<string>('');

  const [scenes, setScenes] = useState<Chapter['scenes']>([]);
  const [selectedScene, setSelectedScene] = useState<string>('');

  const [storyName, setStoryName] = useState('未命名');
  const [storyPlayerInfo, setStoryPlayerInfo] = useState<{
    name: string
  }>({
    name: '未命名',
  });

  useEffect(() => {
    if (storyId) {
      getStoryDiffList(storyId);
    }
  }, [storyId]);

  // getChapters
  const getStoryDiffList = async (id: string) => {
    const { code, data, message } = await getDiffList(id);
    if (code === CgiErrorCode.NO_ERROR) {
      const list = data?.chapterList?.map(e => ({
        value: e?.chapterId,
        label: e?.name || '未命名',
        scenes: e?.sceneList?.map(e => ({
          value: e?.sceneId || '',
          label: e?.name || '未命名',
        })) || [],
      }));
      setChapters(list || []);
      setStoryName(data?.storyName);
      setStoryPlayerInfo(data?.playerInfo);
    } else {
      MessagePlugin.error(message || '故事请求失败，请稍后重试');
    }
    return chapters;
  };

  useEffect(() => {
    if (selectedScene) {
      getSceneInfo(selectedScene);
    }
  }, [selectedScene]);

  const [sceneInfo, setSceneInfo] = useState<any>({});
  const [releaseSceneInfo, setReleaseSceneInfo] = useState<any>({});
  const [missionMergeIds, setMissionMergeIds] = useState<string[]>([]);

  const getSceneInfo = async (id: string) => {
    const { code, data, message } = await getSceneDiffDetail(id);

    if (code === CgiErrorCode.NO_ERROR) {
      setSceneInfo(data?.draftData);
      setReleaseSceneInfo(data?.releaseData);
      getMissionMergeIds(data?.draftData, data?.releaseData);
    } else {
      MessagePlugin.error(message || '修改详情请求失败，请刷新重试');
    }
  };

  const getDiffData = (id: string) => {
    const leftData = releaseSceneInfo?.missionList?.find((e: { mission: { id: string; }; }) => e?.mission?.id === id)?.mission || {};
    const rightData = sceneInfo?.missionList?.find((e: { mission: { id: string; }; }) => e?.mission?.id === id)?.mission || {};

    return {
      leftData: leftData?.missionEvent || [],
      rightData: rightData?.missionEvent || [],
      leftTitle: getName(leftData?.missionType, leftData?.name),
      rightTitle: getName(rightData?.missionType, rightData?.name),
    };
  };

  const getName = (type: MissionType, name: string) => {
    if (type === MissionType.MISSION_TYPE_PROLOGUE) {
      return `开场剧情-【${name || '未命名'}】`;
    }
    if (type === MissionType.MISSION_TYPE_SUCCESS) {
      return '过关剧情';
    }
    if (type === MissionType.MISSION_TYPE_MAINLINE) {
      return `分支剧情-【${name || '未命名'}】`;
    }
    if (type === MissionType.MISSION_TYPE_ENDING) {
      return `结局剧情-【${name || '未命名'}】`;
    }

    return name || '';
  };

  // 处理章节选择变化
  const handleChapterChange = (value: SelectValue) => {
    setSelectedChapter(value as string);
    const sceneList = chapters.find(e => e.value === value)?.scenes || [];
    setScenes(sceneList);
  };

  // 处理幕选择变化
  const handleSceneChange = (value: SelectValue) => {
    setSelectedScene(value as string);
  };

  const getMissionMergeIds = (data: IScene, releaseData: IScene) => {
    const missionDraftIds = data?.missionList?.map((e: { mission: { id: string; }; }) => e?.mission?.id) || [];
    const missionReleaseIds = releaseData?.missionList?.map((e: { mission: { id: string; }; }) => e?.mission?.id) || [];
    // 合并去重
    const missionMergeIds = [...new Set([...missionDraftIds, ...missionReleaseIds])];
    setMissionMergeIds(missionMergeIds);
  };

  return (
    <div className={styles.container}>
      <h3>{storyName}</h3>
      {/* 顶部选择区域 */}
      <div className={styles.selectionArea}>
        <FormItem label={<div style={{ fontSize: 13 }}>选择章节</div>}
          labelAlign='top'
        >
          <Select
            value={selectedChapter}
            onChange={handleChapterChange}
            options={chapters}
            className={styles.chapterSelect}
            placeholder="请选择章节"
          />
        </FormItem>
        <FormItem label={<div style={{ fontSize: 13 }}>选择幕</div>}
          labelAlign='top'
        >
          <Select
            value={selectedScene}
            onChange={handleSceneChange}
            options={scenes}
            className={styles.sceneSelect}
            placeholder="请选择幕"
          />
        </FormItem>
      </div>
      <div className={styles.diffContainer}>
        <div className={styles.left}>
          <div className={styles.sceneContainer}>
            <div className={styles.sceneTitle}>
              已上架
            </div>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.sceneTitle}>
            草稿箱
          </div>
        </div>
      </div>
      {
        missionMergeIds?.map((e, i) => {
          const { leftData, rightData, leftTitle, rightTitle } = getDiffData(e);
          return (
            <div key={`${i + e}`} style={
              { marginBottom: 20 }
            }>
              <DiffJsonView
                leftData={leftData}
                rightData={rightData}
                isEvent={true}
                leftTitle={leftTitle}
                rightTitle={rightTitle}
                bots={
                  {
                    draftBots: sceneInfo?.botPromptList || [],
                    releaseBots: releaseSceneInfo?.botPromptList || [],
                    playerInfo: storyPlayerInfo,
                  }
                }
              />
            </div>
          );
        })
      }
    </div >
  );
};

export default ChapterSceneDiffView;
